import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { gamesAPI, statsAPI, standingsAPI, seasonsAPI } from '../api/api';
import api from '../api/api';
import { useAuth } from '../context/AuthContext';
import LiveStreams from '../components/LiveStreams';

const ICON_MAP = {
  fire: '🔥', star: '⭐', trophy: '🏆', medal: '🏅', crown: '👑',
  lightning: '⚡', shield: '🛡️', sword: '⚔️', rocket: '🚀', basketball: '🏀',
  target: '🎯', diamond: '💎', lock: '🔒', muscle: '💪', brain: '🧠',
  gear: '⚙️', sparkles: '✨', skull: '💀', badge: '🎖️', refresh: '🔄'
};

const Home = () => {
  const { t } = useTranslation();
  const { user, isTeamOwner, isOwner, hasTeam, getOwnedTeamId } = useAuth();
  const [recentGames, setRecentGames] = useState([]);
  const [topScorers, setTopScorers] = useState([]);
  const [topRebounders, setTopRebounders] = useState([]);
  const [topAssists, setTopAssists] = useState([]);
  const [standings, setStandings] = useState({ eastern: [], western: [] });
  const [activeSeason, setActiveSeason] = useState(null);
  const [gmDashboard, setGmDashboard] = useState(null);
  const [activeFreeAgency, setActiveFreeAgency] = useState(null);
  const [rewardsTab, setRewardsTab] = useState('seasonpass');
  const [loading, setLoading] = useState(true);

  const isGmWithTeam = user && (isTeamOwner() || isOwner() || user.role === 'admin') && hasTeam();

  useEffect(() => {
    loadData();
  }, []);

  // Load GM dashboard when user becomes available
  useEffect(() => {
    if (isGmWithTeam && !gmDashboard) {
      seasonsAPI.getGmDashboard(getOwnedTeamId())
        .then(res => setGmDashboard(res.data))
        .catch(err => console.error('Error loading GM dashboard:', err));
    }
  }, [user]);

  const loadData = async () => {
    try {
      // Get active season
      const seasonRes = await seasonsAPI.getActive();
      const season = seasonRes.data;
      setActiveSeason(season);

      // Load all data
      const [gamesRes, leadersRes, standingsRes] = await Promise.all([
        gamesAPI.getRecent(5, season.id), // Pass season ID to get recent games from active season
        statsAPI.getAllLeaders(season.id, 5),
        standingsAPI.get(season.id)
      ]);

      // Fetch free agency status (non-blocking, ignore 404)
      api.get(`/free-agency/season/${season.id}`)
        .then(res => {
          const fa = res.data.freeAgency;
          if (fa && fa.status !== 'completed') {
            setActiveFreeAgency({ ...fa, seasonId: season.id });
          }
        })
        .catch(() => {}); // No FA configured — that's fine

      setRecentGames(gamesRes.data);
      setTopScorers(leadersRes.data.points || []);
      setTopRebounders(leadersRes.data.rebounds || []);
      setTopAssists(leadersRes.data.assists || []);
      setStandings(standingsRes.data);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-96">
        <div className="text-2xl text-gray-600 dark:text-gray-400">{t('common.loading')}</div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-4 md:py-8">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-nba-blue to-nba-red text-white rounded-lg shadow-xl p-6 md:p-8 mb-6 md:mb-8">
        <div className="flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex-1">
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-3 md:mb-4">{t('home.title')}</h1>
            <p className="text-lg md:text-xl mb-2">{t('home.subtitle')}</p>
            <p className="text-base md:text-lg">{t('home.description')}</p>
          </div>
          <img 
            src="https://www.2kbundesliga.de/wp-content/uploads/2022/01/cropped-cropped-2kbuli-header_06.png" 
            alt="2K Bundesliga Logo"
            className="h-24 md:h-32 w-auto object-contain"
            onError={(e) => { e.target.style.display = 'none'; }}
          />
        </div>
      </div>

      {/* Free Agency Banner */}
      {activeFreeAgency && (
        <Link
          to={`/free-agency/${activeFreeAgency.seasonId}`}
          className="block mb-6 md:mb-8"
        >
          <div className={`rounded-lg shadow-lg dark:shadow-gray-900 p-4 md:p-5 flex items-center justify-between gap-4 transition-opacity hover:opacity-90 ${
            activeFreeAgency.status === 'in_progress'
              ? 'bg-gradient-to-r from-yellow-500 to-orange-500 text-white'
              : 'bg-gradient-to-r from-blue-700 to-blue-500 text-white'
          }`}>
            <div className="flex items-center gap-4">
              <span className="text-3xl">🏀</span>
              <div>
                <div className="font-bold text-lg leading-tight">
                  {activeFreeAgency.status === 'in_progress'
                    ? `${activeFreeAgency.name} läuft jetzt!`
                    : `${activeFreeAgency.name} — bald geht's los`}
                </div>
                <div className="text-sm opacity-90 mt-0.5">
                  {activeFreeAgency.status === 'in_progress'
                    ? `Täglich ${activeFreeAgency.dailyStartTime}–${activeFreeAgency.dailyEndTime} Uhr · ${activeFreeAgency.pickTimeMinutes} Min pro Pick`
                    : `Startet am ${new Date(activeFreeAgency.startDate).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })} Uhr`}
                </div>
              </div>
            </div>
            <div className="shrink-0 bg-white dark:bg-gray-800 bg-opacity-20 rounded-lg px-4 py-2 font-semibold text-sm">
              {activeFreeAgency.status === 'in_progress' ? 'Jetzt ansehen →' : 'Details ansehen →'}
            </div>
          </div>
        </Link>
      )}

      {/* GM Dashboard or League Info */}
      {isGmWithTeam && gmDashboard ? (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8 mb-6 md:mb-8">
          {/* Last Game Result */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg dark:shadow-gray-900 p-4 md:p-6">
            <h2 className="text-xl md:text-2xl font-bold mb-3 md:mb-4 text-gray-800 dark:text-gray-200">
              {t('home.gmGreeting', { username: user.username, teamName: user.team?.name || '' })}
            </h2>
            <h3 className="text-lg font-semibold mb-3 text-nba-blue">{t('home.lastGameResult')}</h3>
            {gmDashboard.lastGame ? (() => {
              const game = gmDashboard.lastGame;
              const teamId = getOwnedTeamId();
              const isHome = game.homeTeamId === teamId;
              const myScore = isHome ? game.homeScore : game.awayScore;
              const oppScore = isHome ? game.awayScore : game.homeScore;
              const opponent = isHome ? game.awayTeam : game.homeTeam;
              const isWin = myScore > oppScore;
              return (
                <Link to={`/game/${game.id}`} className="block border rounded-lg p-4 hover:bg-gray-50 dark:bg-gray-800 transition-colors">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <span className={`text-sm font-bold px-2 py-1 rounded ${isWin ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400' : 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400'}`}>
                        {isWin ? t('home.win') : t('home.loss')}
                      </span>
                      <div>
                        <span className="font-semibold">{t('home.vsTeam')} {opponent?.name}</span>
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          {new Date(game.gameDate).toLocaleDateString()}
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      <span className="text-2xl font-bold">{myScore} - {oppScore}</span>
                    </div>
                  </div>
                  <div className="text-xs text-nba-blue mt-2 hover:underline">
                    {t('home.viewBoxScore')} →
                  </div>
                </Link>
              );
            })() : (
              <p className="text-gray-500 dark:text-gray-400 italic">{t('home.noGamesYet')}</p>
            )}
          </div>

          {/* Season Info */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg dark:shadow-gray-900 p-4 md:p-6">
            <h2 className="text-xl md:text-2xl font-bold mb-3 md:mb-4 text-gray-800 dark:text-gray-200">{t('home.seasonOverview')}</h2>
            
            <div className="space-y-3">
              {/* Games Played */}
              <div className="flex justify-between items-center py-2 border-b">
                <span className="text-sm text-gray-600 dark:text-gray-400">{t('home.gamesPlayed')}</span>
                <span className="font-bold">{gmDashboard.gamesPlayed}</span>
              </div>

              {/* Games Left Season */}
              {gmDashboard.season.maxGames && (
                <div className="flex justify-between items-center py-2 border-b">
                  <span className="text-sm text-gray-600 dark:text-gray-400">{t('home.gamesLeftSeason')}</span>
                  <span className="font-bold">{gmDashboard.gamesLeftSeason}</span>
                </div>
              )}

              {/* Games to Playoff Minimum */}
              {gmDashboard.season.minGamesForPlayoffs && (
                <div className="flex justify-between items-center py-2 border-b">
                  <span className="text-sm text-gray-600 dark:text-gray-400">{t('home.gamesToMinPlayoffs')}</span>
                  <span className={`font-bold ${gmDashboard.gamesToMinPlayoffs === 0 ? 'text-green-600 dark:text-green-400' : 'text-orange-600'}`}>
                    {gmDashboard.gamesToMinPlayoffs === 0 ? t('home.qualified') : gmDashboard.gamesToMinPlayoffs}
                  </span>
                </div>
              )}

              {/* Deadlines */}
              {gmDashboard.deadlines.length > 0 ? (
                <>
                  {(() => {
                    const past = gmDashboard.deadlines.filter(d => d.isPast);
                    const active = gmDashboard.deadlines.filter(d => d.isActive);
                    const lastPast = past.length > 0 ? [past[past.length - 1]] : [];
                    return [...lastPast, ...active];
                  })().map((deadline) => {
                    const isActive = deadline.isActive;
                    const isPast = deadline.isPast;
                    return (
                      <div key={deadline.id} className={`rounded-lg p-3 ${isActive ? 'bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700' : isPast ? 'bg-gray-50 dark:bg-gray-800' : 'bg-yellow-50 border border-yellow-200 dark:border-yellow-700'}`}>
                        <div className="flex justify-between items-center mb-1">
                          <span className="font-semibold text-sm">
                            {isActive ? '🔵 ' : isPast ? '⚫ ' : '🟡 '}
                            {deadline.label}
                            {isActive && <span className="text-xs ml-1 text-blue-600 dark:text-blue-400">({t('home.currentDeadline')})</span>}
                          </span>
                          {!isPast ? (
                            <span className="text-xs font-medium text-gray-600 dark:text-gray-400">
                              {t('home.daysToDeadline', { days: deadline.daysLeft })} • {t('home.deadlineEnds', { date: new Date(deadline.endDate).toLocaleDateString() })}
                            </span>
                          ) : (
                            <span className="text-xs text-gray-400">{t('home.deadlinePassed')}</span>
                          )}
                        </div>
                        <div className="grid grid-cols-2 gap-2 text-xs mt-2">
                          {deadline.maxGames !== null && (
                            <div className="flex justify-between">
                              <span className="text-gray-500 dark:text-gray-400">{t('home.gamesLeftDeadline')}:</span>
                              <span className="font-semibold">{deadline.gamesLeftForDeadline}</span>
                            </div>
                          )}
                          <div className="flex justify-between">
                            <span className="text-gray-500 dark:text-gray-400">{t('home.gamesToMinDeadline')}:</span>
                            <span className={`font-semibold ${deadline.gamesToMinDeadline === 0 ? 'text-green-600 dark:text-green-400' : 'text-orange-600'}`}>
                              {deadline.gamesToMinDeadline === 0 ? t('home.deadlineMet') : deadline.gamesToMinDeadline}
                            </span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </>
              ) : (
                <div className="text-sm text-gray-400 italic">{t('home.noDeadlines')}</div>
              )}
            </div>
          </div>

          {/* Rewards Progress (tabbed) */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg dark:shadow-gray-900 p-4 md:p-6">
            <h2 className="text-xl md:text-2xl font-bold mb-3 md:mb-4 text-gray-800 dark:text-gray-200">{t('home.rewardsProgress')}</h2>

            {/* Tabs */}
            <div className="border-b border-gray-200 dark:border-gray-700 mb-4">
              <nav className="-mb-px flex space-x-4">
                <button
                  onClick={() => setRewardsTab('seasonpass')}
                  className={`pb-2 px-1 text-sm font-medium border-b-2 transition-colors ${
                    rewardsTab === 'seasonpass'
                      ? 'border-blue-600 text-blue-600 dark:text-blue-400'
                      : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:text-gray-300 hover:border-gray-300 dark:border-gray-600'
                  }`}
                >
                  {t('home.seasonPassProgress')}
                </button>
                <button
                  onClick={() => setRewardsTab('rookiesoph')}
                  className={`pb-2 px-1 text-sm font-medium border-b-2 transition-colors ${
                    rewardsTab === 'rookiesoph'
                      ? 'border-blue-600 text-blue-600 dark:text-blue-400'
                      : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:text-gray-300 hover:border-gray-300 dark:border-gray-600'
                  }`}
                >
                  {t('home.rookieSophProgress')}
                </button>
                <button
                  onClick={() => setRewardsTab('attributes')}
                  className={`pb-2 px-1 text-sm font-medium border-b-2 transition-colors ${
                    rewardsTab === 'attributes'
                      ? 'border-blue-600 text-blue-600 dark:text-blue-400'
                      : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:text-gray-300 hover:border-gray-300 dark:border-gray-600'
                  }`}
                >
                  {t('home.attributeUpgradesProgress')}
                </button>
              </nav>
            </div>

            {/* Season Pass Tab */}
            {rewardsTab === 'seasonpass' && (
              <>
            {gmDashboard.seasonPass && gmDashboard.seasonPass.levels.length > 0 ? (
              <div className="space-y-4">
                {gmDashboard.seasonPass.teamTier && (
                  <div className="flex items-center gap-2 text-sm">
                    <span className="px-2 py-0.5 rounded bg-indigo-100 dark:bg-indigo-900/40 text-indigo-700 dark:text-indigo-300 font-bold">
                      {gmDashboard.seasonPass.teamTier}
                    </span>
                    <span className="text-gray-500 dark:text-gray-400">{t('home.tierRewardsHint')}</span>
                  </div>
                )}
                {gmDashboard.seasonPass.currentLevel ? (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-2xl">{ICON_MAP[gmDashboard.seasonPass.currentLevel.icon] || '✅'}</span>
                      <span className="text-sm font-semibold text-green-700 dark:text-green-400">{t('home.currentLevelLabel')}</span>
                    </div>
                    <div className="text-lg font-bold text-green-800">
                      Level {gmDashboard.seasonPass.currentLevel.level}
                    </div>
                    <div className="text-sm text-green-700 dark:text-green-400 mt-1">
                      <span className="font-medium">{t('home.rewardLabel')}:</span> {gmDashboard.seasonPass.currentLevel.rewardDescription}
                    </div>
                    {gmDashboard.seasonPass.currentLevel.reward
                      && gmDashboard.seasonPass.currentLevel.reward !== gmDashboard.seasonPass.currentLevel.rewardDescription && (
                      <div className="text-sm text-green-800 dark:text-green-300 mt-1 whitespace-pre-line">
                        {gmDashboard.seasonPass.currentLevel.reward}
                      </div>
                    )}
                    <div className="text-xs text-green-600 dark:text-green-400 mt-1">
                      {gmDashboard.seasonPass.currentLevel.gamesRequired} {t('common.games')}
                    </div>
                  </div>
                ) : (
                  <div className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 text-sm text-gray-500 dark:text-gray-400 italic">
                    {t('home.levelNotReached')}
                  </div>
                )}

                {gmDashboard.seasonPass.nextLevel ? (
                  <div className="bg-yellow-50 border border-yellow-200 dark:border-yellow-700 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-2xl">{ICON_MAP[gmDashboard.seasonPass.nextLevel.icon] || '🔜'}</span>
                      <span className="text-sm font-semibold text-yellow-700 dark:text-yellow-400">{t('home.nextLevelLabel')}</span>
                    </div>
                    <div className="text-lg font-bold text-yellow-800">
                      Level {gmDashboard.seasonPass.nextLevel.level}
                    </div>
                    <div className="text-sm text-yellow-700 dark:text-yellow-400 mt-1">
                      <span className="font-medium">{t('home.rewardLabel')}:</span> {gmDashboard.seasonPass.nextLevel.rewardDescription}
                    </div>
                    {gmDashboard.seasonPass.nextLevel.reward
                      && gmDashboard.seasonPass.nextLevel.reward !== gmDashboard.seasonPass.nextLevel.rewardDescription && (
                      <div className="text-sm text-yellow-800 dark:text-yellow-300 mt-1 whitespace-pre-line">
                        {gmDashboard.seasonPass.nextLevel.reward}
                      </div>
                    )}
                    <div className="mt-2">
                      <div className="flex justify-between text-xs text-yellow-700 dark:text-yellow-400 mb-1">
                        <span>{t('home.gamesToNextLevel', { games: gmDashboard.seasonPass.gamesToNextLevel })}</span>
                        <span>{gmDashboard.gamesPlayed} / {gmDashboard.seasonPass.nextLevel.gamesRequired}</span>
                      </div>
                      <div className="w-full bg-yellow-200 rounded-full h-2.5">
                        <div
                          className="bg-yellow-500 h-2.5 rounded-full transition-all"
                          style={{ width: `${Math.min(100, (gmDashboard.gamesPlayed / gmDashboard.seasonPass.nextLevel.gamesRequired) * 100)}%` }}
                        ></div>
                      </div>
                    </div>
                  </div>
                ) : gmDashboard.seasonPass.currentLevel ? (
                  <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 text-center">
                    <span className="text-2xl">🏆</span>
                    <div className="text-sm font-bold text-purple-700 mt-1">{t('home.allLevelsCompleted')}</div>
                  </div>
                ) : null}

                {gmDashboard.seasonPass.levels.length > 1 && (
                  <div className="flex flex-wrap gap-2 pt-2 border-t border-gray-100 dark:border-gray-700">
                    {gmDashboard.seasonPass.levels.map((lvl) => {
                      const reached = gmDashboard.gamesPlayed >= lvl.gamesRequired;
                      const isNext = gmDashboard.seasonPass.nextLevel && lvl.id === gmDashboard.seasonPass.nextLevel.id;
                      return (
                        <div
                          key={lvl.id}
                          className={`flex items-center gap-1 px-2 py-1 rounded text-xs font-medium ${
                            reached
                              ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
                              : isNext
                                ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400 ring-1 ring-yellow-300'
                                : 'bg-gray-100 dark:bg-gray-700 text-gray-400'
                          }`}
                          title={`Level ${lvl.level} — ${lvl.rewardDescription} (${lvl.gamesRequired} ${t('common.games')})\n\n${lvl.reward || ''}`}
                        >
                          <span>{ICON_MAP[lvl.icon] || `L${lvl.level}`}</span>
                          {reached && <span>✓</span>}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            ) : (
              <div className="text-sm text-gray-400 italic py-4 text-center">{t('home.noSeasonPass')}</div>
            )}
              </>
            )}

            {/* Rookies & Sophs Tab */}
            {rewardsTab === 'rookiesoph' && (
              <>
            {gmDashboard.rookieSoph && gmDashboard.rookieSoph.levels.length > 0 ? (
              <div className="space-y-4">
                {gmDashboard.rookieSoph.currentLevel ? (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-2xl">{ICON_MAP[gmDashboard.rookieSoph.currentLevel.icon] || '✅'}</span>
                      <span className="text-sm font-semibold text-green-700 dark:text-green-400">{t('home.currentLevelLabel')}</span>
                    </div>
                    <div className="text-lg font-bold text-green-800">
                      Level {gmDashboard.rookieSoph.currentLevel.level}
                    </div>
                    <div className="text-sm text-green-700 dark:text-green-400 mt-1">
                      <span className="font-medium">{t('home.upgradeLabel')}:</span> {gmDashboard.rookieSoph.currentLevel.upgradeDescription}
                    </div>
                    {gmDashboard.rookieSoph.currentLevel.limitationDescription && (
                      <div className="text-xs text-green-600 dark:text-green-400 mt-1 italic">
                        <span className="font-medium">{t('home.limitationLabel')}:</span> {gmDashboard.rookieSoph.currentLevel.limitationDescription}
                      </div>
                    )}
                    <div className="text-xs text-green-600 dark:text-green-400 mt-1">
                      {gmDashboard.rookieSoph.currentLevel.gamesRequired} {t('common.games')}
                    </div>
                  </div>
                ) : (
                  <div className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 text-sm text-gray-500 dark:text-gray-400 italic">
                    {t('home.rookieSophNotReached')}
                  </div>
                )}

                {gmDashboard.rookieSoph.nextLevel ? (
                  <div className="bg-yellow-50 border border-yellow-200 dark:border-yellow-700 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-2xl">{ICON_MAP[gmDashboard.rookieSoph.nextLevel.icon] || '🔜'}</span>
                      <span className="text-sm font-semibold text-yellow-700 dark:text-yellow-400">{t('home.nextLevelLabel')}</span>
                    </div>
                    <div className="text-lg font-bold text-yellow-800">
                      Level {gmDashboard.rookieSoph.nextLevel.level}
                    </div>
                    <div className="text-sm text-yellow-700 dark:text-yellow-400 mt-1">
                      <span className="font-medium">{t('home.upgradeLabel')}:</span> {gmDashboard.rookieSoph.nextLevel.upgradeDescription}
                    </div>
                    {gmDashboard.rookieSoph.nextLevel.limitationDescription && (
                      <div className="text-xs text-yellow-600 mt-1 italic">
                        <span className="font-medium">{t('home.limitationLabel')}:</span> {gmDashboard.rookieSoph.nextLevel.limitationDescription}
                      </div>
                    )}
                    <div className="mt-2">
                      <div className="flex justify-between text-xs text-yellow-700 dark:text-yellow-400 mb-1">
                        <span>{t('home.gamesToNextLevel', { games: gmDashboard.rookieSoph.gamesToNextLevel })}</span>
                        <span>{gmDashboard.gamesPlayed} / {gmDashboard.rookieSoph.nextLevel.gamesRequired}</span>
                      </div>
                      <div className="w-full bg-yellow-200 rounded-full h-2.5">
                        <div
                          className="bg-yellow-500 h-2.5 rounded-full transition-all"
                          style={{ width: `${Math.min(100, (gmDashboard.gamesPlayed / gmDashboard.rookieSoph.nextLevel.gamesRequired) * 100)}%` }}
                        ></div>
                      </div>
                    </div>
                  </div>
                ) : gmDashboard.rookieSoph.currentLevel ? (
                  <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 text-center">
                    <span className="text-2xl">🏆</span>
                    <div className="text-sm font-bold text-purple-700 mt-1">{t('home.rookieSophAllCompleted')}</div>
                  </div>
                ) : null}

                {gmDashboard.rookieSoph.levels.length > 1 && (
                  <div className="flex flex-wrap gap-2 pt-2 border-t border-gray-100 dark:border-gray-700">
                    {gmDashboard.rookieSoph.levels.map((lvl) => {
                      const reached = gmDashboard.gamesPlayed >= lvl.gamesRequired;
                      const isNext = gmDashboard.rookieSoph.nextLevel && lvl.id === gmDashboard.rookieSoph.nextLevel.id;
                      return (
                        <div
                          key={lvl.id}
                          className={`flex items-center gap-1 px-2 py-1 rounded text-xs font-medium ${
                            reached
                              ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
                              : isNext
                                ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400 ring-1 ring-yellow-300'
                                : 'bg-gray-100 dark:bg-gray-700 text-gray-400'
                          }`}
                          title={`Level ${lvl.level}: ${lvl.upgradeDescription} (${lvl.gamesRequired} games)`}
                        >
                          <span>{ICON_MAP[lvl.icon] || `L${lvl.level}`}</span>
                          {reached && <span>✓</span>}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            ) : (
              <div className="text-sm text-gray-400 italic py-4 text-center">{t('home.noRookieSoph')}</div>
            )}
              </>
            )}

            {/* Attribute Upgrades Tab */}
            {rewardsTab === 'attributes' && (
              <>
            {gmDashboard.attributeUpgrades ? (
              <div className="space-y-4">
                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg p-4 text-sm text-blue-700 dark:text-blue-400">
                  {t('home.attrRule', {
                    games: gmDashboard.attributeUpgrades.gamesInterval,
                    points: gmDashboard.attributeUpgrades.pointsPerInterval
                  })}
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
                    <div className="text-3xl font-bold text-green-800">{gmDashboard.attributeUpgrades.totalPoints}</div>
                    <div className="text-xs text-green-600 dark:text-green-400 mt-1">{t('home.attrTotalPoints')}</div>
                  </div>
                  <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 text-center">
                    <div className="text-3xl font-bold text-purple-800">{gmDashboard.attributeUpgrades.upgradesEarned}</div>
                    <div className="text-xs text-purple-600 mt-1">{t('home.attrUpgradesEarned')}</div>
                  </div>
                </div>

                {gmDashboard.attributeUpgrades.gamesUntilNext > 0 && (
                  <div className="bg-yellow-50 border border-yellow-200 dark:border-yellow-700 rounded-lg p-4">
                    <div className="text-sm text-yellow-700 dark:text-yellow-400 mb-2">
                      {t('home.attrNextUpgrade', { games: gmDashboard.attributeUpgrades.gamesUntilNext })}
                    </div>
                    <div className="w-full bg-yellow-200 rounded-full h-2.5">
                      <div
                        className="bg-yellow-500 h-2.5 rounded-full transition-all"
                        style={{ width: `${((gmDashboard.attributeUpgrades.gamesInterval - gmDashboard.attributeUpgrades.gamesUntilNext) / gmDashboard.attributeUpgrades.gamesInterval) * 100}%` }}
                      ></div>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-sm text-gray-400 italic py-4 text-center">{t('home.noAttributeUpgrades')}</div>
            )}
              </>
            )}
          </div>
        </div>
      ) : (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg dark:shadow-gray-900 p-4 md:p-6 mb-6 md:mb-8">
          <h2 className="text-2xl md:text-3xl font-bold mb-3 md:mb-4 text-gray-800 dark:text-gray-200">{t('home.aboutLeague')}</h2>
          <p className="text-sm md:text-base text-gray-700 dark:text-gray-300 leading-relaxed mb-3 md:mb-4">
            {t('home.aboutDescription1')}
          </p>
          <p className="text-sm md:text-base text-gray-700 dark:text-gray-300 leading-relaxed">
            {t('home.aboutDescription2')}
          </p>
        </div>
      )}

      {/* Live Streams Section */}
      <LiveStreams />

      {/* Standings Preview */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8 mb-6 md:mb-8">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg dark:shadow-gray-900 p-4 md:p-6">
          <h2 className="text-xl md:text-2xl font-bold mb-3 md:mb-4 text-nba-blue">{t('home.eastStandings')}</h2>
          <div className="overflow-x-auto mobile-scroll">
            <table className="w-full text-xs md:text-sm">
              <thead className="bg-gray-100 dark:bg-gray-700">
                <tr>
                  <th className="px-2 md:px-4 py-2 text-left">#</th>
                  <th className="px-2 md:px-4 py-2 text-left">Team</th>
                  <th className="px-2 md:px-4 py-2 text-center">W</th>
                  <th className="px-2 md:px-4 py-2 text-center">L</th>
                  <th className="px-2 md:px-4 py-2 text-center">PCT</th>
                </tr>
              </thead>
              <tbody>
                {standings.eastern.slice(0, 6).map((team, index) => (
                  <tr key={team.team.id} className="border-b hover:bg-gray-50 dark:bg-gray-800">
                    <td className="px-2 md:px-4 py-2 font-semibold">{index + 1}</td>
                    <td className="px-2 md:px-4 py-2">
                      <Link 
                        to={`/teams/${team.team.id}`}
                        className="flex items-center gap-2 hover:text-nba-blue transition"
                      >
                        <img 
                          src={`/images/teams/${team.team.name.toLowerCase().replace(/\s/g, '')}.svg`}
                          alt={`${team.team.name} logo`}
                          className="w-6 h-6 object-contain flex-shrink-0"
                          onError={(e) => { e.target.style.display = 'none'; }}
                        />
                        <span className="truncate">{team.team.name}</span>
                      </Link>
                    </td>
                    <td className="px-2 md:px-4 py-2 text-center">{team.wins}</td>
                    <td className="px-2 md:px-4 py-2 text-center">{team.losses}</td>
                    <td className="px-2 md:px-4 py-2 text-center">{team.winPct}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Link to="/standings" className="block text-center mt-3 md:mt-4 text-nba-blue hover:underline font-semibold text-sm md:text-base">
            {t('home.viewFullStandings')}
          </Link>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg dark:shadow-gray-900 p-4 md:p-6">
          <h2 className="text-xl md:text-2xl font-bold mb-3 md:mb-4 text-nba-red">{t('home.westStandings')}</h2>
          <div className="overflow-x-auto mobile-scroll">
            <table className="w-full text-xs md:text-sm">
              <thead className="bg-gray-100 dark:bg-gray-700">
                <tr>
                  <th className="px-2 md:px-4 py-2 text-left">#</th>
                  <th className="px-2 md:px-4 py-2 text-left">{t('common.team')}</th>
                  <th className="px-2 md:px-4 py-2 text-center">W</th>
                  <th className="px-2 md:px-4 py-2 text-center">L</th>
                  <th className="px-2 md:px-4 py-2 text-center">PCT</th>
                </tr>
              </thead>
              <tbody>
                {standings.western.slice(0, 6).map((team, index) => (
                  <tr key={team.team.id} className="border-b hover:bg-gray-50 dark:bg-gray-800">
                    <td className="px-2 md:px-4 py-2 font-semibold">{index + 1}</td>
                    <td className="px-2 md:px-4 py-2">
                      <Link 
                        to={`/teams/${team.team.id}`}
                        className="flex items-center gap-2 hover:text-nba-red transition"
                      >
                        <img 
                          src={`/images/teams/${team.team.name.toLowerCase().replace(/\s/g, '')}.svg`}
                          alt={`${team.team.name} logo`}
                          className="w-6 h-6 object-contain flex-shrink-0"
                          onError={(e) => { e.target.style.display = 'none'; }}
                        />
                        <span className="truncate">{team.team.name}</span>
                      </Link>
                    </td>
                    <td className="px-2 md:px-4 py-2 text-center">{team.wins}</td>
                    <td className="px-2 md:px-4 py-2 text-center">{team.losses}</td>
                    <td className="px-2 md:px-4 py-2 text-center">{team.winPct}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Link to="/standings" className="block text-center mt-3 md:mt-4 text-nba-red hover:underline font-semibold text-sm md:text-base">
            {t('home.viewFullStandings')}
          </Link>
        </div>
      </div>

      {/* Recent Games & Stats */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8">
        {/* Recent Games */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg dark:shadow-gray-900 p-4 md:p-6">
          <h2 className="text-xl md:text-2xl font-bold mb-3 md:mb-4 text-gray-800 dark:text-gray-200">
            {t('home.recentGames')}
            {activeSeason && (
              <span className="text-lg md:text-xl font-normal text-gray-600 dark:text-gray-400 ml-2">
                - {activeSeason.name}
              </span>
            )}
          </h2>
          <div className="space-y-3">
            {recentGames.map((game) => (
              <Link
                key={game.id}
                to={`/game/${game.id}`}
                className="block border-b pb-3 hover:bg-gray-50 dark:bg-gray-800 transition-colors rounded px-2 py-1"
              >
                <div className="flex justify-between items-center">
                  <div className="flex-1 min-w-0">
                    {game.isPlayoff && (
                      <div className="mb-1">
                        <span className="text-xs font-bold uppercase tracking-wide bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400 px-2 py-0.5 rounded-full">
                          🏆 Playoffs{game.playoffRound ? ` · ${game.playoffRound}` : ''}
                        </span>
                      </div>
                    )}
                    <div className="font-semibold text-sm md:text-base truncate">{game.awayTeam.name}</div>
                    <div className="font-semibold text-sm md:text-base truncate">{game.homeTeam.name}</div>
                  </div>
                  <div className="text-right ml-2 flex-shrink-0">
                    <div className={`text-lg md:text-xl font-bold ${game.awayScore > game.homeScore ? 'text-green-600 dark:text-green-400' : ''}`}>
                      {game.awayScore}
                    </div>
                    <div className={`text-lg md:text-xl font-bold ${game.homeScore > game.awayScore ? 'text-green-600 dark:text-green-400' : ''}`}>
                      {game.homeScore}
                    </div>
                  </div>
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  {new Date(game.gameDate).toLocaleDateString()} • {t('home.clickForBoxScore')}
                </div>
              </Link>
            ))}
          </div>
          <Link 
            to="/games" 
            className="block text-center mt-3 md:mt-4 text-blue-600 dark:text-blue-400 hover:underline text-sm md:text-base"
          >
            {t('home.viewAllGames')}
          </Link>
        </div>

        {/* Top Performers */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg dark:shadow-gray-900 p-4 md:p-6">
          <h2 className="text-xl md:text-2xl font-bold mb-3 md:mb-4 text-gray-800 dark:text-gray-200">{t('home.leagueLeaders')}</h2>
          
          <div className="mb-4">
            <h3 className="font-semibold text-base md:text-lg mb-2 text-nba-blue">{t('home.pointsPerGame')}</h3>
            {topScorers.slice(0, 3).filter(p => p.player).map((player, index) => (
              <div key={index} className="flex justify-between py-1">
                <Link 
                  to={`/player/${player.player.id}`}
                  className="text-blue-600 dark:text-blue-400 hover:underline text-sm md:text-base truncate mr-2"
                >
                  {player.player.firstName} {player.player.lastName}
                </Link>
                <span className="font-bold text-sm md:text-base whitespace-nowrap">{player.ppg}</span>
              </div>
            ))}
          </div>

          <div className="mb-4">
            <h3 className="font-semibold text-base md:text-lg mb-2 text-nba-blue">{t('home.reboundsPerGame')}</h3>
            {topRebounders.slice(0, 3).filter(p => p.player).map((player, index) => (
              <div key={index} className="flex justify-between py-1">
                <Link 
                  to={`/player/${player.player.id}`}
                  className="text-blue-600 dark:text-blue-400 hover:underline text-sm md:text-base truncate mr-2"
                >
                  {player.player.firstName} {player.player.lastName}
                </Link>
                <span className="font-bold text-sm md:text-base whitespace-nowrap">{player.rpg}</span>
              </div>
            ))}
          </div>

          <div>
            <h3 className="font-semibold text-base md:text-lg mb-2 text-nba-blue">{t('home.assistsPerGame')}</h3>
            {topAssists.slice(0, 3).filter(p => p.player).map((player, index) => (
              <div key={index} className="flex justify-between py-1">
                <Link 
                  to={`/player/${player.player.id}`}
                  className="text-blue-600 dark:text-blue-400 hover:underline text-sm md:text-base truncate mr-2"
                >
                  {player.player.firstName} {player.player.lastName}
                </Link>
                <span className="font-bold text-sm md:text-base whitespace-nowrap">{player.apg}</span>
              </div>
            ))}
          </div>

          <Link to="/stats" className="block text-center mt-3 md:mt-4 text-nba-blue hover:underline font-semibold text-sm md:text-base">
            {t('home.viewAllStats')}
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Home;
